// Modal de envío de documentos por correo. Ofrece plantillas de mensaje
// predefinidas y, si la carpeta tiene un documento "Comunicaciones" (Word),
// lo usa como cuerpo por defecto convirtiéndolo de HTML a texto plano.
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { X, Send, Mail, FileText, FileSpreadsheet, File, Loader, CheckCircle, AlertCircle, Paperclip } from "lucide-react";
import { api } from "../api";
import { EMAIL_TEMPLATES } from "../emailTemplates";
import { formatDate } from "../utils/dates";
import { parseEmailList } from "../utils/emails";

// Convierte el HTML del "Comunicaciones" a texto plano legible en un
// correo (inserta saltos de línea donde había bloques/párrafos).
function htmlToText(html) {
  const div = document.createElement("div");
  div.innerHTML = DOMPurify.sanitize(html);
  div.querySelectorAll("p, br, li, div, tr, h1, h2, h3").forEach(el => el.insertAdjacentText("afterend", "\n"));
  return div.textContent.replace(/\n{3,}/g, "\n\n").trim();
}


const TYPE_ICON = {
  pdf:  <FileText size={14} style={{ color: "#E53E3E" }} />,
  xlsx: <FileSpreadsheet size={14} style={{ color: "#22863a" }} />,
  xls:  <FileSpreadsheet size={14} style={{ color: "#22863a" }} />,
  docx: <File size={14} style={{ color: "var(--primary)" }} />,
  doc:  <File size={14} style={{ color: "var(--primary)" }} />,
};

function fileExt(doc) {
  return (doc.filename || doc.originalname || "").split(".").pop().toLowerCase();
}

function fmtSize(b) {
  if (!b) return "";
  return b > 1e6 ? `${(b/1e6).toFixed(1)} MB` : `${(b/1e3).toFixed(0)} KB`;
}


export default function EmailModal({ docs, comunicado, onClose }) {
  const today = formatDate(new Date().toISOString(), { month: "long" });

  const defaultTemplate = EMAIL_TEMPLATES[0];

  // Solo se puede usar como plantilla si es un Word (se puede leer su HTML);
  // un Excel/PDF de nombre "Comunicaciones" simplemente se ignora aquí.
  const comunicadoExt = comunicado ? fileExt(comunicado) : null;
  const comunicadoUsable = comunicado && ["docx", "doc"].includes(comunicadoExt);

  const [comunicadoText, setComunicadoText]       = useState(null);
  const [comunicadoLoading, setComunicadoLoading] = useState(comunicadoUsable);

  const templates = comunicadoUsable
    ? [{
        id: "comunicado",
        label: `📁 Comunicado de "${comunicado.category}"`,
        subject: (d) => defaultTemplate.subject(d),
        body: () => comunicadoText ?? "",
      }, ...EMAIL_TEMPLATES]
    : EMAIL_TEMPLATES;

  const initialTemplate = comunicadoUsable ? templates[0] : defaultTemplate;

  const [templateId, setTemplateId] = useState(initialTemplate.id);
  const [form, setForm]         = useState({
    to: "",
    subject: initialTemplate.subject(docs),
    body: initialTemplate.body(docs, today),
  });
  const [status, setStatus]     = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [progress, setProgress] = useState("");
  const [sendResult, setSendResult] = useState(null); // { sent, total, failed }

  const { valid: toEmails, invalid: invalidEmails } = parseEmailList(form.to);

  // Carga el contenido del documento "Comunicaciones" de la carpeta, si aplica,
  // y lo deja listo como mensaje predeterminado.
  useEffect(() => {
    if (!comunicadoUsable) return;
    let cancelled = false;
    setComunicadoLoading(true);
    api.getContent(comunicado.id).then(data => {
      if (cancelled) return;
      if (data?.type === "word" && data.html) {
        const text = htmlToText(data.html);
        setComunicadoText(text);
        setForm(p => ({ ...p, body: text }));
      }
    }).finally(() => { if (!cancelled) setComunicadoLoading(false); });
    return () => { cancelled = true; };
  }, [comunicado?.id]);

  const handleTemplateChange = (id) => {
    setTemplateId(id);
    const template = templates.find(t => t.id === id) || defaultTemplate;
    setForm(p => ({ ...p, subject: template.subject(docs), body: template.body(docs, today) }));
  };

  const handleSend = async () => {
    if (!toEmails.length) return;
    setStatus("sending");
    setProgress(toEmails.length > 1 ? `Enviando a ${toEmails.length} destinatarios...` : "Enviando correo...");

    try {
      const result = await api.sendEmail({
        docIds: docs.map(d => d.id),
        to: toEmails,
        subject: form.subject,
        body: form.body + "\n\n---\nSistema DocSST | Gestión Documental SST",
      });
      if (result.error) throw new Error(result.error);
      setSendResult(result);
      setStatus(result.failed?.length ? "partial" : "ok");
    } catch (e) {
      setErrorMsg(e.message);
      setStatus("error");
    }
  };

  return (
    <motion.div className="modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <motion.div className="modal" style={{ maxWidth: 600 }}
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}>

        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#F0FDFA", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Mail size={18} style={{ color: "var(--primary)" }} />
            </div>
            <div>
              <h2>Enviar por Gmail</h2>
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                {docs.length === 1 ? docs[0].name : `${docs.length} documentos seleccionados`}
              </p>
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        {status === "ok" && (
          <div className="modal-success">
            <CheckCircle size={48} className="success-icon" />
            <p>{sendResult?.total > 1 ? `¡${sendResult.sent} correos enviados correctamente!` : "¡Correo enviado correctamente!"}</p>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", textAlign: "center" }}>
              {sendResult?.total > 1 ? "Cada destinatario" : "El destinatario"} recibirá el correo con {docs.length === 1 ? "el archivo" : `los ${docs.length} archivos`} adjunto{docs.length !== 1 ? "s" : ""}.
            </p>
            <button className="btn-secondary" onClick={onClose}>Cerrar</button>
          </div>
        )}

        {status === "partial" && (
          <div className="modal-success">
            <AlertCircle size={48} style={{ color: "#D97706" }} />
            <p>{sendResult.sent} de {sendResult.total} correos enviados</p>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", textAlign: "center", maxWidth: 380 }}>
              No se pudieron enviar a: {sendResult.failed.map(f => f.to).join(", ")}
            </p>
            <button className="btn-secondary" onClick={onClose}>Cerrar</button>
          </div>
        )}

        {status === "fallback" && (
          <div className="modal-success">
            <Mail size={48} style={{ color: "var(--primary)" }} />
            <p>Gmail abierto en el navegador</p>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", textAlign: "center", maxWidth: 340 }}>
              Adjunta los archivos manualmente desde la carpeta <strong>sst-backend/uploads/</strong> y haz clic en Enviar.
            </p>
            <button className="btn-secondary" onClick={onClose}>Cerrar</button>
          </div>
        )}

        {status === "sending" && (
          <div style={{ padding: "48px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
            <Loader size={40} className="spin" style={{ color: "var(--primary)" }} />
            <p style={{ fontWeight: 500 }}>{progress}</p>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Esto puede tardar unos segundos...</p>
          </div>
        )}

        {!status && (
          <>
            {/* Adjuntos */}
            <div style={{ padding: "16px 24px 0" }}>
              <p style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                <Paperclip size={12} style={{ display: "inline", marginRight: 4 }} />
                Archivos adjuntos ({docs.length})
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                {docs.map(d => (
                  <span key={d.id} style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "4px 10px", background: "var(--bg)",
                    border: "1px solid var(--border)", borderRadius: 6,
                    fontSize: "0.75rem", color: "var(--text)",
                  }}>
                    {TYPE_ICON[fileExt(d)]}
                    {d.name}
                    <span style={{ color: "var(--text-muted)", fontSize: "0.65rem" }}>
                      {d.version} · {fmtSize(d.size)}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            <div className="modal-form" style={{ padding: "0 24px 8px" }}>
              <div className="form-row">
                <label>Mensaje predeterminado</label>
                <select className="form-input" value={templateId}
                  onChange={e => handleTemplateChange(e.target.value)}>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label>Para *</label>
                <textarea className="form-input" rows={3}
                  placeholder="correo@empresa.com&#10;Pega aquí varios correos: uno por línea, o separados por coma/punto y coma"
                  value={form.to} onChange={e => setForm(p => ({ ...p, to: e.target.value }))} autoFocus />
                {form.to.trim() && (
                  <p style={{ fontSize: "0.72rem", color: invalidEmails.length ? "#C53030" : "var(--text-muted)", marginTop: 4 }}>
                    {toEmails.length} destinatario{toEmails.length !== 1 ? "s" : ""} detectado{toEmails.length !== 1 ? "s" : ""}
                    {invalidEmails.length > 0 && ` · se ignoraron ${invalidEmails.length} no válido${invalidEmails.length !== 1 ? "s" : ""}: ${invalidEmails.join(", ")}`}
                  </p>
                )}
              </div>
              <div className="form-row">
                <label>Asunto</label>
                <input className="form-input" value={form.subject}
                  onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} />
              </div>
              <div className="form-row">
                <label>Mensaje</label>
                {templateId === "comunicado" && comunicadoLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", color: "var(--text-muted)", fontSize: "0.8rem" }}>
                    <Loader size={14} className="spin" /> Cargando comunicado de la carpeta...
                  </div>
                ) : (
                  <textarea className="form-input form-textarea" rows={8}
                    value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} />
                )}
              </div>

              {status === "error" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem" }}>
                  <AlertCircle size={15} /> {errorMsg}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Cancelar</button>
              <button className="btn-primary" onClick={handleSend}
                disabled={!toEmails.length || (templateId === "comunicado" && comunicadoLoading)}>
                <Send size={15} /> Enviar con adjuntos{toEmails.length > 1 ? ` (${toEmails.length})` : ""}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
